import { effect, EventEmitter, inject, Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, of, Subscription } from 'rxjs';
import { noPermissions, RoleDefinition } from './role-model';
import Keycloak from 'keycloak-js';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs } from 'keycloak-angular';
import { AuthProvider, ConnectionService, StorageService } from '@luzula/luzula-lib';

interface AuthImpl {
	init(): void;
	login$(): Observable<void>;
	logout$(): Observable<void>;

	user$(): Observable<any | null | undefined>;

	isAuthenticated$(): Observable<boolean>;
	isAuthenticated(): boolean;

	access(): Observable<any>;

	getKeycloak(): Keycloak;
	notifyAuthenticated(value: boolean): void;
}

interface AuthInfo {
	roles: string[];
	realmRoles: string[];
	matrix: object;
	currentUser: string;
	currentUserId: string;
	currentRole: string;
}

class KeycloakImpl implements AuthImpl {
	private readonly impl = inject(Keycloak);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
	private readonly storage = inject(StorageService);
  authenticated$ = new BehaviorSubject<boolean>(false);
  keycloakStatus: string | undefined;
  private initialized = false;

  constructor() {
    effect(() => {
      const keycloakEvent = this.keycloakSignal();

      this.keycloakStatus = keycloakEvent.type;
      if (keycloakEvent.type === KeycloakEventType.Ready) {
				const auth = typeEventArgs<ReadyArgs>(keycloakEvent.args);
				if (auth && this.impl.token) 
					this.storage.setSync('kc-token', this.impl.token);
				else this.storage.removeSync('kc-token');
				if (auth && this.impl.refreshToken) 
					this.storage.setSync('kc-refresh-token', this.impl.refreshToken);
				else this.storage.removeSync('kc-refresh-token');
        this.authenticated$.next(auth);
      }

      if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
				this.storage.removeSync('kc-token');
				this.storage.removeSync('kc-refresh-token');
        this.authenticated$.next(false);
      }
    });
  }
	
	init() {
		if (this.initialized) return;
		this.initialized = true;
		this.impl.init(
			{
				onLoad: 'check-sso',
				silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
				redirectUri: window.location.origin + '/',
				token: this.storage.getSync('kc-token') || undefined,
				refreshToken: this.storage.getSync('kc-refresh-token') || undefined,
			}
		);
	}
	
	user$() {
		return of(this.impl.tokenParsed);
	}
	
	login$() {
		return from(this.impl.login());
	}
	
	logout$() {
		this.storage.removeSync('kc-token');
		this.storage.removeSync('kc-refresh-token');
		return from(this.impl.logout());
	}
	
	isAuthenticated$() {
		return this.authenticated$;
	}
	
	isAuthenticated(): boolean {
		return !!this.impl.authenticated;
	}
	
	access() {
		return of({ realm: this.impl.realmAccess, resources: this.impl.resourceAccess });
	}

	getKeycloak(): Keycloak {
		return this.impl;
	}

	notifyAuthenticated(value: boolean): void {
		this.authenticated$.next(value);
	}
}


@Injectable({
	providedIn: 'root'
})
export class AuthService implements AuthProvider {
	authImpl: AuthImpl = new KeycloakImpl();
	
	commenting = false;
	pen: 'danger'|'warning'|'message'|'rubber' = 'danger';
	currentRole = '';
	private roleRanking = new Array<string>('entwickler', 'manager', 'kartierer', 'kontrolleur', 'datennutzer', 'gast');
	public currentUser = '';

	// wraps logic about being authenticated in an offline fassion
	public isAuthenticated = new BehaviorSubject<boolean>(false);
	private isOnline: boolean = navigator.onLine;
	
	public loggedOut = new EventEmitter<void>;
	public loggedIn = new EventEmitter<void>;
	public loginFailed = new EventEmitter<void>;
	public authChanged = new EventEmitter<void>;
	
	private matrix = noPermissions;
	private roles = new Set<string>;
	private realmRoles = new Set<string>;
	public currentUserId = '';
	private authSub?: Subscription;

	constructor( 
		private cs: ConnectionService,
		private storage: StorageService
	) {
		this.loggedIn.subscribe(() => this.authChanged.emit());
		this.loggedOut.subscribe(() => this.authChanged.emit());
		this.loginFailed.subscribe(() => this.authChanged.emit());
	}

	bestRole(): string|undefined {
		const br = this.roleRanking.find((r: string) => this.roles.has(r));
		console.log(`best role: ${br}`);
		return br;
	}
	
	private storeAuthInfo() {
		const inf: any = {
			currentUser: this.currentUser,
			currentUserId: this.currentUserId,
			currentRole: this.currentRole,
			roles: [...this.roles],
			realmRoles: [...this.realmRoles],
			matrix: { } as any
		};
		for (const k of this.matrix?.keys()) {
			inf.matrix[k] = [...this.matrix.get(k)!];
		}
		this.storage.setSync('ai', btoa(JSON.stringify(inf)));
	}
	
	private retrieveAuthInfo(emitChange = true): boolean {
		const inf = this.storage.getSync('ai');
		let success = false;
		if (inf) {
			try {
				const decoded = atob(inf);
				const parsed = JSON.parse(decoded);
				this.currentUser = parsed.currentUser;
				this.currentUserId = parsed.currentUserId || '';
				this.currentRole = parsed.currentRole;
				this.roles = new Set(parsed.roles);
				this.realmRoles = new Set(parsed.realmRoles || []);
				this.matrix = new Map<string,Set<string>>;
				for (const k of Object.keys(parsed.matrix))
					this.matrix.set(k, new Set<string>(parsed.matrix[k]));
				success = true;
			} catch (e) {
				console.warn('Failed to parse auth info:', e);
			}
		}
		if (emitChange) this.authChanged.emit();
		return success;
	}
	
	public init(online: boolean = navigator.onLine, onAppLoad = false) {
		if (onAppLoad) {
			// Wenn bereits bekannt ist dass wir offline sind (z.B. Offline-Modus),
			// sofort initialisieren ohne auf Netzwerk-Check zu warten
			if (!online) {
				this.isOnline = false;
				// Emit=false: checkAuthentication() emittiert authChanged selbst
				this.retrieveAuthInfo(false);
				this.checkAuthentication();
				return;
			}
			// Sonst auf Netzwerk-Status warten
			this.cs.monitor().subscribe((online: boolean) => {
				this.isOnline = online;
				// Emit=false: checkAuthentication() emittiert authChanged selbst
				this.retrieveAuthInfo(false);
				if (online) this.authImpl.init();
				this.checkAuthentication();
			});
		}
	}
	
	private checkAuthentication() {
		if (this.isOnline) {
			// Vorherige Subscription aufräumen um Race-Conditions zu vermeiden
			this.authSub?.unsubscribe();
			this.authSub = this.authImpl.isAuthenticated$().subscribe((authenticated: boolean) => {
				if (authenticated) {
					this.authImpl.user$().subscribe((user: any) => {
						if (user) {
							this.currentUser = user.name;
							this.currentUserId = user.sub;
							this.authImpl.access().subscribe((access: any) => {
								// Capture realm roles
								const realmRoles = access?.realm?.roles as string[];
								realmRoles?.forEach((r: string) => this.realmRoles.add(r));

								// Capture client roles
								const permissions = access?.resources?.bpweb?.roles as string[];
								permissions?.forEach((p: string) => {
									if (p.startsWith('role:')) {
										const role = p.substring(5);
										this.roles.add(role);
										return;
									}
								});
								const doAuth = (role: string) => {
									this.switchRole(role, false);
									this.loggedIn.emit();
									console.log(`done auth ${role}`);
									this.isAuthenticated.next(true);
								};

								const currentRole = this.currentRole;
								if (currentRole && this.roles.has(currentRole) && RoleDefinition.has(currentRole)) {
									this.matrix = RoleDefinition.get(currentRole)!;
									doAuth(currentRole);
								}
								else if (this.bestRole())
									doAuth(this.bestRole()!);
								else
									doAuth('gast');
							});
						}
					});
				}
				else {
					this.checkOfflineAuthentication();
				}
			});
		}
		else {
			this.checkOfflineAuthentication();
		}
	}
	
	private checkOfflineAuthentication() {
		this.isAuthenticated.next(this.retrieveAuthInfo());
	}
	
	isAuthenticated$(): Observable<boolean> {
		if (this.isOnline)
			return of(this.authImpl.isAuthenticated());
		return this.isAuthenticated;
	}
	
	tryAuthenticate() {
		return this.authImpl.isAuthenticated$();
	}
	
	public onLoggedOut() {
		// currentRole NICHT auf 'gast' setzen — logout() hat es bereits auf '' gesetzt.
		// Sonst würde checkAuthentication() nach Re-Login 'gast' beibehalten
		// statt bestRole() zu verwenden.
		this.storage.removeSync('ai');
		this.loggedOut.emit();
	}
	
	public user() {
		return this.authImpl.isAuthenticated() 
			? this.authImpl.user$() 
			: of({name: this.currentUser });
	}
	
	public login() {
		return this.authImpl.login$();
	}
	
	private set(resource: string, access: string) {
		if (!resource || !access) return;
		if (!this.matrix.has(resource))
			this.matrix.set(resource, new Set<string>);
		this.matrix.get(resource)!.add(access);
	}
	
	public canI(access: string, resource: string) {
		if (!this.matrix.has(resource))
			return false;
		const res = this.matrix.get(resource);
		return res!.has(access);
	}
	
	public amI(role: string) {
		return this.currentRole === role;
	}
	
	public canIbe(role: string) {
		return this.roles.has(role);
	}
	
	preferCommenting() {
		return this.amI('kontrolleur');
	}
	
	canEditAndComment() {
		return (this.canIbe('kontrolleur') && this.canIbe('kartierer')) || this.canIbe('entwickler');
	}
	
	switchRole(role: string, emit = true) {
		if (!this.canIbe(role) || !RoleDefinition.has(role))
			return;
		this.currentRole = role;
		this.matrix = RoleDefinition.get(role)!;
		this.storeAuthInfo();
		this.commenting = this.preferCommenting();
		if (emit) this.authChanged.emit();
	}
	
	getRoles() {
		return this.roles;
	}

	hasRealmRole(role: string): boolean {
		return this.realmRoles.has(role);
	}

	getRealmRoles() {
		return this.realmRoles;
	}

	logout(callback: any) {
		this.storage.removeSync('ai');
		this.storage.removeSync('kc-token');
		this.storage.removeSync('kc-refresh-token');

		this.currentUser = '';
		this.currentUserId = '';
		this.currentRole = '';
		this.roles.clear();
		this.realmRoles.clear();
		this.matrix = noPermissions;
		this.isAuthenticated.next(false);
		this.authImpl.notifyAuthenticated(false);

		if (callback) callback();

		// SSO-Session serverseitig beenden (Redirect zur Keycloak-Logout-Seite)
		if (this.isOnline) {
			this.authImpl.logout$().subscribe();
		}
	}

}
